import { useTranslation } from "react-i18next";

import { FormHelperText, List, Stack } from "@mui/material";
import { FormikProps } from "formik";

import { FileUploadArea } from "../../../../../../file-upload-area/FileUploadArea";
import { AssetDocumentZone, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import {
  useOptionalAssetInventoryDocumentsApi,
  useOptionalDocumentDndApi,
} from "../../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../../query/clientFileQueryKeys";
import { useAssetInventoryDocuments } from "../../../../hooks/useAssetInventoryDocuments";
import { AssetPersonalDataFormValues } from "../../AssetPersonalData.types";
import { BankStatementRow } from "./BankStatementRow";

interface Props {
  clientId: string;
  formik: FormikProps<AssetPersonalDataFormValues>;
  saveAttempted: boolean;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

const SECTION = "clients.client.debtSettlement.assetInventory.section1.bankStatement";

export function BankStatementUploadBlock({ clientId, formik, saveAttempted, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const dndApi = useOptionalDocumentDndApi();
  const { documents, isFetched, markRead, deleteDocument, isBusy } = useAssetInventoryDocuments(clientId, {
    queryKey: ClientFileQueryKey.AssetInventoryBankStatements,
    getDocuments: (api, params) => api.getAssetInventoryBankStatements(params),
    getDeleteDocument: (api) => api.deleteAssetInventoryBankStatement.bind(api),
  });

  const pendingFiles = formik.values.bankStatements;

  const onDrop = (files: File[]) => {
    void formik.setFieldValue("bankStatements", [...pendingFiles, ...files]);
  };

  const onFileRemove = (index: number) => {
    void formik.setFieldValue(
      "bankStatements",
      pendingFiles.filter((_, i) => i !== index)
    );
  };

  // Optional slice: an app without the asset-inventory document endpoints renders the personal-data
  // form without the bank-statement upload block.
  if (!dndApi && !documentsApi) {
    return null;
  }

  const hasNoStatements =
    saveAttempted &&
    formik.values.hasOwnAccount === true &&
    isFetched &&
    documents.length === 0 &&
    pendingFiles.length === 0;

  return (
    <Stack flex={1} p={2} gap={1} sx={{ bgcolor: "background.paper" }}>
      {/* The internal app injects the full upload-area section (drop target, rows with the app's file
          actions, pending moves) through the documentDnd slice; the portal gets a plain upload area. */}
      {dndApi ? (
        <dndApi.AssetDocumentSection
          clientId={clientId}
          zone={AssetDocumentZone.BankStatement}
          hint={t(`${SECTION}.uploadHint`)}
          pendingFiles={pendingFiles}
          documentMoves={formik.values.documentMoves}
          onDropFiles={onDrop}
          onRemovePending={onFileRemove}
          onAddMove={onAddMove}
          onCancelMove={onCancelMove}
        />
      ) : (
        <FileUploadArea
          title={t(`${SECTION}.title`)}
          hint={t(`${SECTION}.uploadHint`)}
          onDropFiles={onDrop}
          pendingFiles={pendingFiles}
          onRemovePending={onFileRemove}
        >
          {documents.length > 0 && (
            <List disablePadding sx={{ width: "100%" }}>
              {documents.map((statement) => (
                <BankStatementRow
                  key={statement.documentId}
                  statement={statement}
                  onMarkRead={markRead}
                  onDelete={deleteDocument}
                  isBusy={isBusy}
                />
              ))}
            </List>
          )}
        </FileUploadArea>
      )}
      {hasNoStatements && (
        <FormHelperText error role="status" sx={{ m: 0 }}>
          {t(`${SECTION}.missing`)}
        </FormHelperText>
      )}
    </Stack>
  );
}
